import { useState, ChangeEvent, FormEvent } from "react";

export type EnquiryValues = {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
};

type Errors = Partial<Record<keyof EnquiryValues, string>>;
type Status = "idle" | "submitting" | "success" | "error";

const initial: EnquiryValues = { name: "", email: "", phone: "", service: "", message: "" };

export function useEnquiryForm() {
  const [values, setValues] = useState<EnquiryValues>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>("idle");

  const onChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name as keyof EnquiryValues]) {
      setErrors((err) => ({ ...err, [name]: undefined }));
    }
  };

  const setField = (name: keyof EnquiryValues, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((err) => ({ ...err, [name]: undefined }));
  };

  const validate = (): Errors => {
    const next: Errors = {};
    if (!values.name.trim()) next.name = "Please enter your full name.";
    if (!values.email.trim()) next.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) next.email = "Please enter a valid email address.";
    if (!values.service) next.service = "Please choose an area of law.";
    if (values.message.trim().length < 10) next.message = "Please add a few words about your matter.";
    return next;
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus("submitting");
    window.setTimeout(() => setStatus("success"), 1100);
  };

  const reset = () => {
    setValues(initial);
    setErrors({});
    setStatus("idle");
  };

  return { values, errors, status, onChange, setField, onSubmit, reset };
}
